// src/pages/AuthPage.js
import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, User, ArrowRight, AlertCircle } from 'lucide-react';
import { useApp, isValidEmail } from '../context/AppContext';
import Loader from '../components/Loader';

export default function AuthPage() {
  const [mode,         setMode]         = useState('login');
  const [showPassword, setShowPassword] = useState(false);
  const [loading,      setLoading]      = useState(false);
  const [form,         setForm]         = useState({ name: '', email: '', password: '', confirmPassword: '' });
  const [errors,       setErrors]       = useState({});
  const [generalError, setGeneralError] = useState('');

  const { login, signup } = useApp();
  const navigate          = useNavigate();
  const location          = useLocation();
  const redirectTo        = location.state?.from?.pathname || '/';

  const change = (field) => (e) => {
    setForm((f) => ({ ...f, [field]: e.target.value }));
    setErrors((er) => ({ ...er, [field]: '' }));
    setGeneralError('');
  };

  const validate = () => {
    const errs = {};

    if (mode === 'signup' && !form.name.trim()) {
      errs.name = 'Full name is required';
    }

    if (!form.email.trim()) {
      errs.email = 'Email is required';
    } else if (!isValidEmail(form.email)) {
      errs.email = 'Enter a valid email address (e.g. you@example.com)';
    }

    if (!form.password) {
      errs.password = 'Password is required';
    } else if (form.password.length < 6) {
      errs.password = 'Password must be at least 6 characters';
    }

    if (mode === 'signup' && form.password !== form.confirmPassword) {
      errs.confirmPassword = "Passwords don't match";
    }

    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setGeneralError('');

    const errs = validate();
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }

    setLoading(true);
    try {
      if (mode === 'login') {
        await login(form.email, form.password);
      } else {
        await signup(form.email, form.password, form.name);
      }
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setGeneralError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    setErrors({});
    setGeneralError('');
    setForm({ name: '', email: '', password: '', confirmPassword: '' });
  };

  const inputCls = (field) =>
    `w-full pl-10 pr-4 py-3.5 rounded-2xl border font-body text-gray-800 focus:outline-none transition-all ${
      errors[field]
        ? 'border-red-300 bg-red-50 focus:border-red-400'
        : 'border-rose-200 bg-rose-50/30 focus:border-rose-400 focus:bg-white'
    }`;

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center p-4 pt-24">
      {loading && <Loader fullPage />}

      <div className="w-full max-w-md">
        <div className="bg-white rounded-3xl shadow-card p-8 md:p-10">

          {/* Logo */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center gap-2 mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-rose-400 to-rose-600 rounded-full flex items-center justify-center shadow-rose">
                <span className="text-white text-base">♡</span>
              </div>
              <span className="font-display font-bold text-2xl text-gray-900">
                Couple<span className="text-rose-500">Cart</span>
              </span>
            </Link>
            <h1 className="font-display font-bold text-2xl text-gray-900 mb-1">
              {mode === 'login' ? 'Welcome back! 💕' : 'Create your account ✨'}
            </h1>
            <p className="text-gray-400 font-body text-sm">
              {mode === 'login' ? 'Sign in to continue' : 'Join thousands of happy couples'}
            </p>
          </div>

          {/* Tabs */}
          <div className="flex bg-rose-50 rounded-2xl p-1 mb-6">
            {['login', 'signup'].map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => switchMode(m)}
                className={`flex-1 py-2.5 rounded-xl text-sm font-body font-semibold transition-all capitalize ${
                  mode === m
                    ? 'bg-white text-rose-600 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {m === 'login' ? 'Login' : 'Sign Up'}
              </button>
            ))}
          </div>

          {/* General Error */}
          {generalError && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-600 rounded-2xl px-4 py-3 text-sm font-body mb-5">
              <AlertCircle size={16} className="shrink-0 mt-0.5" />
              <span>{generalError}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" noValidate>

            {/* Name (signup only) */}
            {mode === 'signup' && (
              <div>
                <label className="block text-xs font-body font-semibold text-gray-600 mb-1.5">Full Name</label>
                <div className="relative">
                  <User className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={17} />
                  <input
                    type="text"
                    value={form.name}
                    onChange={change('name')}
                    placeholder="Your full name"
                    autoComplete="name"
                    className={inputCls('name')}
                  />
                </div>
                {errors.name && (
                  <p className="text-red-500 text-xs mt-1 font-body flex items-center gap-1">
                    <AlertCircle size={12} /> {errors.name}
                  </p>
                )}
              </div>
            )}

            {/* Email */}
            <div>
              <label className="block text-xs font-body font-semibold text-gray-600 mb-1.5">Email Address</label>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={17} />
                <input
                  type="email"
                  value={form.email}
                  onChange={change('email')}
                  placeholder="you@example.com"
                  autoComplete="email"
                  className={inputCls('email')}
                />
              </div>
              {errors.email && (
                <p className="text-red-500 text-xs mt-1 font-body flex items-center gap-1">
                  <AlertCircle size={12} /> {errors.email}
                </p>
              )}
            </div>

            {/* Password */}
            <div>
              <label className="block text-xs font-body font-semibold text-gray-600 mb-1.5">Password</label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={17} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={form.password}
                  onChange={change('password')}
                  placeholder="Min. 6 characters"
                  autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                  className={`${inputCls('password')} pr-12`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </div>
              {errors.password && (
                <p className="text-red-500 text-xs mt-1 font-body flex items-center gap-1">
                  <AlertCircle size={12} /> {errors.password}
                </p>
              )}
            </div>

            {/* Confirm Password (signup only) */}
            {mode === 'signup' && (
              <div>
                <label className="block text-xs font-body font-semibold text-gray-600 mb-1.5">Confirm Password</label>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" size={17} />
                  <input
                    type="password"
                    value={form.confirmPassword}
                    onChange={change('confirmPassword')}
                    placeholder="Re-enter your password"
                    autoComplete="new-password"
                    className={inputCls('confirmPassword')}
                  />
                </div>
                {errors.confirmPassword && (
                  <p className="text-red-500 text-xs mt-1 font-body flex items-center gap-1">
                    <AlertCircle size={12} /> {errors.confirmPassword}
                  </p>
                )}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-rose-500 hover:bg-rose-600 text-white py-4 rounded-2xl font-body font-semibold text-base transition-all shadow-rose hover:shadow-rose-lg mt-2 disabled:opacity-60"
            >
              {mode === 'login' ? 'Sign In' : 'Create Account'}
              <ArrowRight size={18} />
            </button>
          </form>

          {/* Switch mode */}
          <p className="text-center text-gray-400 font-body text-sm mt-6">
            {mode === 'login' ? "Don't have an account?" : 'Already have an account?'}{' '}
            <button
              type="button"
              onClick={() => switchMode(mode === 'login' ? 'signup' : 'login')}
              className="text-rose-500 hover:text-rose-600 font-semibold"
            >
              {mode === 'login' ? 'Sign up free' : 'Login'}
            </button>
          </p>

          {mode === 'signup' && (
            <p className="text-center text-gray-300 font-body text-xs mt-3">
              A verification email will be sent to your inbox.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
